import { apiGet, apiPut, apiPost, apiDelete } from '.';
import {
  BareSailing,
  VenueConflicts,
  SailingDetails,
  BareSimpleSailing,
  BareSailingDay,
} from '../../../shared/types/sailing';
import { BuildSailingDayTemplateOptions } from '../../../shared/types/template';
import { startOfMonthLocal, endOfMonthLocal, dateToIsoLocal } from '../../../shared/lib/time';
import { ContentTemplateParams } from '@go-shared/types/contentTemplate';

// WARNING: Modifies passed `bareSailing`
function formatSailing(bareSailing: BareSailing | BareSimpleSailing) {
  // convert dates from strings to actual dates
  bareSailing.days.forEach(day => {
    day.date = new Date(String(day.date));
  });
}

export async function getSailing(sailingId: string): Promise<BareSailing> {
  let bareSailing = await apiGet<BareSailing>(`/sailing/${sailingId}`);
  formatSailing(bareSailing);
  return bareSailing;
}

export async function getSailings(
  propertyId: string,
  since?: string,
  until?: string
): Promise<BareSailing[]> {
  let queryParams: any = {};
  if (since) queryParams.from = since;
  if (until) queryParams.to = until;

  let bareSailings = await apiGet<BareSailing[]>(`/itinerary/${propertyId}`, queryParams);
  bareSailings.forEach(formatSailing);
  return bareSailings;
}

export async function getSailingsByTemplateFragment(
  templateId: string,
  from: string
): Promise<BareSailing[]> {
  let queryParams: any = {
    from,
  };
  const bareSailings = await apiGet<BareSailing[]>(
    `/template/${templateId}/itinerary`,
    queryParams
  );
  bareSailings.forEach(formatSailing);
  return bareSailings;
}

export async function getSimpleSailingsByProperty(
  propertyId: string,
  since?: string,
  until?: string
): Promise<BareSimpleSailing[]> {
  let queryParams: any = {};
  if (since) queryParams.from = since;
  if (until) queryParams.to = until;

  let bareSimpleSailings = await apiGet<BareSimpleSailing[]>(
    `/itinerary-simple/${propertyId}`,
    queryParams
  );
  bareSimpleSailings.forEach(formatSailing);
  return bareSimpleSailings;
}

export async function getSailingsByMonth(
  propertyId: string,
  month: number,
  year: number
): Promise<BareSailing[]> {
  let queryParams: any = {};

  const date = new Date(year, month, 1);

  queryParams.from = dateToIsoLocal(startOfMonthLocal(date));
  queryParams.to = dateToIsoLocal(endOfMonthLocal(date));

  let bareSailings = await apiGet<BareSailing[]>(`/itinerary/${propertyId}`, queryParams);
  bareSailings.forEach(formatSailing);
  return bareSailings;
}

export async function getSimpleSailingsByMonth(
  propertyId: string,
  month: number,
  year: number
): Promise<BareSimpleSailing[]> {
  let queryParams: any = {};

  const date = new Date(year, month, 1);

  queryParams.from = dateToIsoLocal(startOfMonthLocal(date));
  queryParams.to = dateToIsoLocal(endOfMonthLocal(date));

  let bareSimpleSailings = await apiGet<BareSimpleSailing[]>(
    `/itinerary-simple/${propertyId}`,
    queryParams
  );
  bareSimpleSailings.forEach(formatSailing);
  return bareSimpleSailings;
}

export async function getSailingDetails(
  propertyId: string,
  since?: string,
  until?: string
): Promise<SailingDetails[]> {
  let queryParams: any = {};
  if (since) queryParams.from = since;
  if (until) queryParams.to = until;

  let sailingDetails = await apiGet<SailingDetails[]>(
    `/itinerary/${propertyId}/details`,
    queryParams
  );
  return sailingDetails;
}

export async function saveSailingAsTemplate(sailingId: string): Promise<BareSailing> {
  return apiPost(`/sailing/${sailingId}/template`);
}

export async function publishSailing(sailingId: string): Promise<BareSailing> {
  return apiPost(`/sailing/${sailingId}/publish`);
}

export async function publishSailingDay(
  sailingId: string,
  sailingDayId: string
): Promise<BareSailingDay> {
  return apiPost(`/sailing/${sailingId}/day/${sailingDayId}/publish`);
}

export async function saveSailingDayAsTemplate(
  sailingId: string,
  sailingDayId: string
): Promise<BareSailing> {
  let bareSailing = await apiPost<BareSailing>(
    `/sailing/${sailingId}/day/${sailingDayId}/template`
  );
  formatSailing(bareSailing);
  return bareSailing;
}

export async function createTemplateFromSailingDay(
  sailingId: string,
  sailingDayId: string,
  options?: BuildSailingDayTemplateOptions
): Promise<BareSailing> {
  let bareSailing = await apiPost<BareSailing>(
    `/sailing/${sailingId}/day/${sailingDayId}/create-template`,
    options
  );
  formatSailing(bareSailing);
  return bareSailing;
}

export async function deleteSailingDayTemplate(
  sailingId: string,
  sailingDayId: string
): Promise<BareSailing> {
  let bareSailing = await apiDelete<BareSailing>(
    `/sailing/${sailingId}/day/${sailingDayId}/template`
  );
  formatSailing(bareSailing);
  return bareSailing;
}

export async function updateSailing(sailingId: string, sailing: BareSailing): Promise<BareSailing> {
  let bareSailing = await apiPut<BareSailing>(`/sailing/${sailingId}`, sailing);
  formatSailing(bareSailing);
  return bareSailing;
}

export async function createSailing(sailing: BareSailing): Promise<BareSailing> {
  let bareSailing = await apiPost<BareSailing>(`/sailing`, sailing);
  formatSailing(bareSailing);
  return bareSailing;
}

export async function deleteSailing(sailingId: string): Promise<boolean> {
  await apiDelete<BareSailing>(`/sailing/${sailingId}`);
  return true;
}

export async function lockSailingDay(
  sailingId: string,
  sailingDayId: string
): Promise<BareSailing> {
  let bareSailing = await apiPost<BareSailing>(`/sailing/${sailingId}/day/${sailingDayId}/lock`);
  formatSailing(bareSailing);
  return bareSailing;
}

export async function unlockSailingDay(
  sailingId: string,
  sailingDayId: string
): Promise<BareSailing> {
  let bareSailing = await apiDelete<BareSailing>(`/sailing/${sailingId}/day/${sailingDayId}/lock`);
  formatSailing(bareSailing);
  return bareSailing;
}

export async function setSailingTemplate(
  sailingId: string,
  planTemplateId: string
): Promise<BareSailing> {
  let bareSailing = await apiPut<BareSailing>(`/sailing/${sailingId}/template/${planTemplateId}`);
  formatSailing(bareSailing);
  return bareSailing;
}

export async function setSailingDayTemplate(
  sailingId: string,
  sailingDayId: string,
  templateId: string,
  force: boolean = false
): Promise<BareSailing> {
  let bareSailing = await apiPut<BareSailing>(
    `/sailing/${sailingId}/day/${sailingDayId}/template/${templateId}`,
    undefined,
    { force }
  );
  formatSailing(bareSailing);
  return bareSailing;
}

export async function assignFeedContent(
  sailingId: string,
  sailingDayId: string,
  areaId: string,
  contentId: string,
  feedSlotNumber: number,
  contentParams?: ContentTemplateParams
): Promise<BareSailing> {
  let bareSailing = await apiPut<BareSailing>(
    `/sailing/${sailingId}/day/${sailingDayId}/feed/${areaId}/content/${contentId}`,
    {
      feedSlotNumber,
      contentParams,
    }
  );

  formatSailing(bareSailing);
  return bareSailing;
}

export async function assignRundownFontSize(
  propertyId: string,
  sailingId: string,
  sailingDayId: string,
  areaId: string,
  fontSize: number,
  feedSlotNumber: number
): Promise<BareSailing> {
  let bareSailing = await apiPut<BareSailing>(
    `property/${propertyId}/sailing/${sailingId}
    /day/${sailingDayId}/feed/${areaId}/size/${fontSize}`,
    { feedSlotNumber }
  );
  formatSailing(bareSailing);
  return bareSailing;
}

export async function getVenueConflicts(
  sailingId: string,
  venueId: string
): Promise<VenueConflicts> {
  return apiGet(`/sailing/${sailingId}/conflicts/${venueId}`);
}

export function exportSailing(
  propertyId: string,
  sailingId: string,
  version: 1 | 2
): Promise<{ location: string }> {
  return apiPost(`/property/${propertyId}/sailing/${sailingId}/export`, { version });
}
